import { Users } from 'lucide-react';
import type { GuestSummary } from '@peles/shared';

export type GuestProgressTab = 'guests' | 'invites' | 'seating';

export interface GuestProgressCardProps {
  summary: GuestSummary;
  /** Opens the tab the next step is done on. */
  onOpenTab: (tab: GuestProgressTab) => void;
  /** Tabs this client has: a next step on a tab they lack is only described. */
  canInvite?: boolean;
  canSeat?: boolean;
}

interface Step {
  label: string;
  value: number;
  of: number;
}

interface NextStep {
  text: string;
  action?: { label: string; tab: GuestProgressTab };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The one thing to do next, walking the list from the start: add guests,
 * invite them, wait for answers, seat those coming.
 */
export function nextGuestStep(
  summary: GuestSummary,
  { canInvite = false, canSeat = false }: { canInvite?: boolean; canSeat?: boolean } = {},
): NextStep {
  const { parties, invitedParties, byResponse, unseatedAttendingParties } = summary;
  if (parties === 0) {
    return { text: 'Start your guest list', action: { label: 'Add guests', tab: 'guests' } };
  }
  const toInvite = parties - invitedParties;
  if (toInvite > 0) {
    return {
      text: `${plural(toInvite, 'guest', 'guests')} still to invite`,
      action: canInvite ? { label: 'Send invitations', tab: 'invites' } : undefined,
    };
  }
  if (byResponse.PENDING > 0) {
    return {
      text: `Waiting on ${plural(byResponse.PENDING, 'answer', 'answers')}`,
      action: canInvite ? { label: 'Send a reminder', tab: 'invites' } : undefined,
    };
  }
  if (unseatedAttendingParties > 0) {
    return {
      text: `${plural(unseatedAttendingParties, 'guest still needs', 'guests still need')} a seat`,
      action: { label: canSeat ? 'Seat them' : 'See the seating', tab: 'seating' },
    };
  }
  return { text: 'Everyone coming has a seat' };
}

/**
 * Where the guest list stands, as a client sees it: invited → answered →
 * coming → seated, each a share of the one before, and the next thing to do.
 */
export function GuestProgressCard({
  summary,
  onOpenTab,
  canInvite,
  canSeat,
}: GuestProgressCardProps) {
  const answered = summary.parties - summary.byResponse.PENDING;
  const steps: Step[] = [
    { label: 'Invited', value: summary.invitedParties, of: summary.parties },
    { label: 'Answered', value: answered, of: summary.invitedParties },
    { label: 'Coming', value: summary.byResponse.YES, of: answered },
    // Seated is a share of those coming, the step before it.
    { label: 'Seated', value: summary.seatedParties, of: summary.byResponse.YES },
  ];
  const next = nextGuestStep(summary, { canInvite, canSeat });

  return (
    <section
      aria-label="Guest progress"
      className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4"
      data-testid="guest-progress"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Users size={16} className="text-gray-400" />
          Guest progress
        </h2>
        <span className="text-sm text-gray-500">
          {plural(summary.parties, 'party', 'parties')} ·{' '}
          {plural(summary.attendingPeople, 'person', 'people')} coming
        </span>
      </div>

      <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {steps.map((step) => {
          const share =
            step.of > 0 ? Math.round((Math.min(step.value, step.of) / step.of) * 100) : 0;
          return (
            <li
              key={step.label}
              className="space-y-1.5"
              data-testid={`guest-progress-${step.label}`}
            >
              <p className="text-xs text-gray-500">{step.label}</p>
              <p className="text-xl font-bold text-gray-900">
                {step.value}
                <span className="text-sm font-medium text-gray-400"> / {step.of}</span>
              </p>
              <span
                className="block h-1.5 rounded-full bg-gray-100 overflow-hidden"
                role="progressbar"
                aria-label={step.label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={share}
              >
                <span
                  className="block h-full rounded-full bg-primary-600"
                  style={{ width: `${share}%` }}
                />
              </span>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-primary-50 px-4 py-3">
        <p className="text-sm font-semibold text-primary-800" data-testid="guest-progress-next">
          {next.text}
        </p>
        {next.action && (
          <button
            type="button"
            onClick={() => onOpenTab(next.action!.tab)}
            className="inline-flex items-center h-9 px-3 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
          >
            {next.action.label}
          </button>
        )}
      </div>
    </section>
  );
}
